import { useRef } from 'react';
import { captureException, captureMessage } from '@sentry/react';
import { useHistory } from 'react-router-dom';
import {
  ApplePay,
  Card,
  CashAppPay,
  GooglePay,
  Payments,
  TokenResult,
} from '@square/web-payments-sdk-types';
import { trackCdp } from 'utilities/tracking';
import { PAYMENT_RESPONSE_ERRORS } from 'typescript/constants/mappings';
import { usePaymentContext } from 'typescript/context/payment_context';
import { useLogCompleteOrderAction } from 'typescript/apps/checkout_v3/hooks/use_log_complete_order_action';
import { useSavedCardsContext } from 'typescript/context/saved_cards';
import {
  createShopPaymentWithSavedCard,
  createShopPaymentWithToken,
  checkPreAuthFraud,
} from 'typescript/utilities/shop_payments';
import {
  useCompleteOrder,
  useGetOrderSummary,
  useUpdateOrderAddressInformation,
} from 'typescript/hooks/use_order_actions_client';
import {
  PaymentSchedule,
  PaymentType,
  PreAuthFraudDecision,
} from 'typescript/constants/enums/payment';
import {
  CHECKOUT_ROUTES,
  CompleteOrderError,
} from 'typescript/constants/enums/checkout';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';
import {
  buildBillingAddressFromBillingContact,
  removePrefixFromAddress,
} from 'typescript/utilities/address_utils';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useSubscriptionDisclosure } from 'typescript/apps/checkout_v3/hooks/use_subscription_disclosure';
import CDP_EVENT_NAMES from 'typescript/constants/cdp_events';
import { COMPONENT_NAMES, FEATURES } from 'typescript/constants/tracking';
import { BILLING_PREFIX } from 'typescript/schemas/address_forms/address_fields';
import { OrderAddress } from 'typescript/types/api/solidshop';

export type PaymentMethod = Card | ApplePay | CashAppPay | GooglePay;

const useSubmitOrder = (orderNumber: string) => {
  const paymentPostalCodeRef = useRef('');

  const history = useHistory();
  const { trackingData } = useCheckoutFlow();
  const { setCompleteOrderError, setShowCompletingOrderDialog } =
    useCompleteOrderContext();
  const {
    setSubmittingPayment,
    subscriptionConsentGiven,
    setSubscriptionConsentError,
  } = usePaymentContext();
  const { consentRequired: subscriptionConsentRequired } =
    useSubscriptionDisclosure();
  const {
    orderSummaryData,
    isPending: orderDataIsPending,
    isError: orderDataIsError,
  } = useGetOrderSummary({ orderNumber });
  const {
    payWithSavedCard,
    setPayWithSavedCard,
    makeDefaultCard,
    saveCardToWallet,
    selectedCard,
  } = useSavedCardsContext();
  const { completeOrder } = useCompleteOrder({ orderNumber });
  const { updateOrderAddressInformationAsync } =
    useUpdateOrderAddressInformation({
      orderNumber,
    });
  const { logCompleteOrderAction } = useLogCompleteOrderAction({
    orderNumber,
    orderSummaryData,
  });

  const paymentSchedule = orderSummaryData?.payment_due
    ?.payment_schedule as PaymentSchedule;

  const tokenize = async ({
    paymentMethod,
    paymentType,
    cashAppTokenResult = undefined,
  }: {
    paymentMethod: PaymentMethod;
    paymentType: PaymentType;
    cashAppTokenResult?: TokenResult;
  }) => {
    let tokenResult: TokenResult;

    if (cashAppTokenResult && paymentType === PaymentType.CASH_APP_PAY) {
      tokenResult = cashAppTokenResult;
    } else if (
      !selectedCard ||
      paymentType === PaymentType.GOOGLE_PAY ||
      paymentType === PaymentType.APPLE_PAY
    ) {
      tokenResult = await (
        paymentMethod as Card | ApplePay | GooglePay
      ).tokenize();
    }

    return tokenResult;
  };

  const getPreAuthFraudDecision = async ({
    paymentType,
    tokenResult,
  }: {
    paymentType: PaymentType;
    tokenResult: TokenResult;
  }) => {
    let decision = PreAuthFraudDecision.DECLINE;

    // Don't check pre-auth fraud if cash app or saved card
    if (
      paymentType !== PaymentType.CASH_APP_PAY &&
      !(paymentType === PaymentType.CARD && selectedCard)
    ) {
      const response = await checkPreAuthFraud(orderNumber, tokenResult);
      if (response?.ok) {
        const responseData = await response.json();
        decision = responseData?.decision as PreAuthFraudDecision;
      }
    } else {
      decision = PreAuthFraudDecision.APPROVE;
    }

    return decision;
  };

  const createShopPayment = async ({
    paymentType,
    tokenResult,
    sqPayments = undefined,
    billingAddress = undefined,
  }: {
    paymentType: PaymentType;
    tokenResult: TokenResult;
    sqPayments?: Payments;
    billingAddress?: OrderAddress;
  }) => {
    let success = false;

    if (selectedCard && paymentType === PaymentType.CARD) {
      setShowCompletingOrderDialog(true);

      try {
        const response = await createShopPaymentWithSavedCard(
          orderNumber,
          selectedCard,
        );
        if (response.ok) {
          setPayWithSavedCard(true);
          success = true;
        } else {
          setCompleteOrderError(CompleteOrderError.GENERIC);
        }
      } catch {
        setCompleteOrderError(CompleteOrderError.GENERIC);
      }
    } else if (tokenResult && tokenResult.status === 'OK') {
      setShowCompletingOrderDialog(true);

      paymentPostalCodeRef.current =
        tokenResult.details?.billing?.postalCode || '';

      // This should never happen since orderData should be fetched/cached before submission
      if (orderDataIsPending || orderDataIsError) {
        captureMessage('Order data is not available for payment submission', {
          extra: {
            isError: orderDataIsError,
            isPending: orderDataIsPending,
          },
        });

        if (orderDataIsError) {
          setCompleteOrderError(CompleteOrderError.GENERIC);
          return success;
        }
      }

      try {
        const response = await createShopPaymentWithToken(
          orderNumber,
          orderSummaryData,
          tokenResult,
          sqPayments,
          null,
          saveCardToWallet,
          billingAddress,
        );
        if (response.ok) {
          setPayWithSavedCard(false);
          success = true;
        }
      } catch {
        setCompleteOrderError(CompleteOrderError.GENERIC);
      }
    }

    return success;
  };

  const updateBillingAddress = async ({
    billingAddress,
  }: {
    billingAddress?: Record<string, string | boolean>;
  }) => {
    const address = { ...billingAddress, billing_address_only: true };

    // Failures here should generally not happen, though can depend on data
    // returned from Web Payment SDK tokenize(). Do not block order submission
    // on billing address update failure and just log the error.
    try {
      await updateOrderAddressInformationAsync({ address });
    } catch (error) {
      captureException(error);
    }
  };

  const submitOrder = async ({
    paymentMethod,
    paymentType,
    billingAddress = undefined,
    sqPayments = undefined,
    cashAppTokenResult = undefined,
    financingDisclosureAcknowledged = false,
    subscriptionConsentPreVerified = false,
  }: {
    paymentMethod: PaymentMethod;
    paymentType: PaymentType;
    billingAddress?: Record<string, string | boolean>;
    sqPayments?: Payments;
    cashAppTokenResult?: TokenResult;
    financingDisclosureAcknowledged?: boolean;
    // Set on the mobile Cash App Pay redirect-return path only: consent was
    // already enforced before the redirect launched (the Cash App button is not
    // attached until the checkbox is checked), but the return lands on a fresh
    // page where the in-memory consent state is gone. Without this, the gate
    // below would re-block and strand the buyer on a spinner.
    subscriptionConsentPreVerified?: boolean;
  }) => {
    // Legal gate: opt_out carts require affirmative subscription consent.
    if (
      subscriptionConsentRequired &&
      !subscriptionConsentGiven &&
      !subscriptionConsentPreVerified
    ) {
      setSubscriptionConsentError(true);
      return;
    }

    setSubmittingPayment(true);

    logCompleteOrderAction({
      status: 'initiated',
      paymentType,
      paymentSchedule,
    });

    const tokenResult = await tokenize({
      paymentMethod,
      paymentType,
      cashAppTokenResult,
    });

    const preAuthFraudDecision = await getPreAuthFraudDecision({
      paymentType,
      tokenResult,
    });
    if (preAuthFraudDecision !== PreAuthFraudDecision.APPROVE) {
      logCompleteOrderAction({
        status: 'error',
        paymentType,
        paymentSchedule,
        errorStage: 'pre_auth_fraud',
        errorDetails: `decision: ${preAuthFraudDecision}`,
      });
      setCompleteOrderError(CompleteOrderError.PRE_AUTH);
      setSubmittingPayment(false);
      setShowCompletingOrderDialog(false);
      return;
    }

    const billingAddressWithPrefix =
      paymentType === PaymentType.CARD
        ? billingAddress
        : buildBillingAddressFromBillingContact({
            billingContact: tokenResult?.details?.billing,
          });

    const createShopPaymentSuccess = await createShopPayment({
      paymentType,
      tokenResult,
      sqPayments,
      billingAddress: removePrefixFromAddress(
        billingAddressWithPrefix,
        BILLING_PREFIX,
      ),
    });

    if (createShopPaymentSuccess) {
      await updateBillingAddress({
        billingAddress: billingAddressWithPrefix,
      });

      completeOrder(
        {
          recaptchaResponse: false.toString(),
          paymentPostalCode: paymentPostalCodeRef.current,
          useSavedPaymentSource:
            paymentType === PaymentType.CARD && payWithSavedCard,
          makeDefaultCard: paymentType === PaymentType.CARD && makeDefaultCard,
          financingDisclosureAcknowledged,
        },
        {
          onSuccess: () => {
            logCompleteOrderAction({
              status: 'success',
              paymentType,
              paymentSchedule,
            });
            // set the state upon success to hide the completing order dialog
            setSubmittingPayment(false);
            setShowCompletingOrderDialog(false);
          },
          onError: (error) => {
            const errorName = error?.name;
            const errorMessage = error?.message;
            const errorDetails = errorMessage
              ? `${errorName}: ${errorMessage}`
              : errorName;

            let cdpEventName;

            // if it is a payment error, set the payment submission error
            if (Object.values(PAYMENT_RESPONSE_ERRORS).includes(errorMessage)) {
              cdpEventName =
                CDP_EVENT_NAMES.CHECKOUT_V3.PAYMENT
                  .COMPLETING_ORDER_ERROR_PAYMENT;
              setCompleteOrderError(CompleteOrderError.PAYMENT);
            } else {
              cdpEventName =
                CDP_EVENT_NAMES.CHECKOUT_V3.PAYMENT
                  .COMPLETING_ORDER_ERROR_PAYMENT;
              // if it isn't a payment error, then it's some order completion error which we show a generic message for
              setCompleteOrderError(CompleteOrderError.GENERIC);
            }

            trackCdp({
              name: cdpEventName,
              params: {
                feature: FEATURES.SHOP.NAME,
                step: COMPONENT_NAMES.APPS.CHECKOUT_V3.PAYMENT,
                error: errorDetails,
                ...trackingData,
              },
            });

            logCompleteOrderAction({
              status: 'error',
              paymentType,
              paymentSchedule,
              errorStage: 'complete_order',
              errorDetails,
            });

            // reset the submitting payment state so they can try again
            setSubmittingPayment(false);
            setShowCompletingOrderDialog(false);
          },
          onSettled: (_data, error) => {
            // if there are no payment errors, redirect to the complete page
            if (!error) {
              history.push(CHECKOUT_ROUTES.COMPLETE);
            }
          },
        },
      );
    } else {
      logCompleteOrderAction({
        status: 'error',
        paymentType,
        paymentSchedule,
        errorStage: 'create_payment',
        errorDetails: 'create_payment_failed',
      });
      setSubmittingPayment(false);
      setShowCompletingOrderDialog(false);
    }
  };

  return { submitOrder };
};

export { useSubmitOrder };
